import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { auth, db } from "../../backend/firebase";
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import Dataset from "../../components/Dataset";
import {
  FiPlus,
  FiGitPullRequest,
  FiChevronRight,
  FiCalendar,
  FiLogIn,
  FiDatabase,
  FiSettings,
  FiFolder,
  FiUser,
  FiMail,
  FiActivity,
  FiCopy,
  FiCheck,
  FiEye,
  FiDownload,
  FiHeart,
  FiRefreshCw,
  FiTrendingUp,
  FiUsers,
  FiStar,
} from "react-icons/fi";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "./Profile.css";

const Profile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = auth?.currentUser;
  const metadata = user?.metadata;
  const [dataSets, setDataSets] = useState([]);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  const [analytics, setAnalytics] = useState({
    totalViews: 0,
    totalDownloads: 0,
    totalLikes: 0,
    uniqueViewers: 0,
    viewsByDataset: [],
    downloadsByDataset: [],
    likesByDataset: [],
    recentActivity: [],
    performanceOverTime: [],
    topPerformingDatasets: [],
  });

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      let dateObj;
      if (date instanceof Timestamp) {
        dateObj = date.toDate();
      } else if (date instanceof Date) {
        dateObj = date;
      } else if (typeof date === "string" || typeof date === "number") {
        dateObj = new Date(date);
      } else {
        return "Invalid Date";
      }
      return dateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.warn("Date formatting error:", error);
      return "N/A";
    }
  };

  const copyUserId = () => {
    navigator.clipboard.writeText(auth.currentUser.uid);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const fetchUserAnalytics = useCallback(async (datasetIds) => {
    if (!datasetIds.length) {
      setAnalytics({
        totalViews: 0,
        totalDownloads: 0,
        totalLikes: 0,
        uniqueViewers: 0,
        viewsByDataset: [],
        downloadsByDataset: [],
        likesByDataset: [],
        recentActivity: [],
        performanceOverTime: [],
        topPerformingDatasets: [],
      });
      return;
    }

    try {
      const dailyStatsMap = new Map();
      const today = new Date();

      for (let i = 6; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        const dateKey = d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        dailyStatsMap.set(dateKey, {
          date: dateKey,
          views: 0,
          downloads: 0,
          likes: 0,
        });
      }

      const analyticsData = {
        totalViews: 0,
        totalDownloads: 0,
        totalLikes: 0,
        uniqueViewers: new Set(),
        viewsByDataset: [],
        downloadsByDataset: [],
        likesByDataset: [],
        recentActivity: [],
        performanceOverTime: [],
        topPerformingDatasets: [],
      };

      const allPromises = datasetIds.map(async (dataset) => {
        try {
          const viewsRef = collection(db, "datasets", dataset.uuid, "Views");
          const viewsSnapshot = await getDocs(viewsRef);
          const viewsCount = viewsSnapshot.size;

          let lastView = null;

          viewsSnapshot.docs.forEach((doc) => {
            const data = doc.data();

            analyticsData.uniqueViewers.add(doc.id);

            if (data.viewedOn) {
              const dateObj =
                data.viewedOn instanceof Timestamp
                  ? data.viewedOn.toDate()
                  : new Date(data.viewedOn);
              const dateKey = dateObj.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              });

              if (dailyStatsMap.has(dateKey)) {
                dailyStatsMap.get(dateKey).views += 1;
              }

              if (!lastView || dateObj > lastView.timestamp) {
                lastView = {
                  timestamp: data.viewedOn,
                  userId: doc.id,
                };
              }
            }
          });

          const downloadsRef = collection(
            db,
            "datasets",
            dataset.uuid,
            "downloads"
          );
          const downloadsSnapshot = await getDocs(downloadsRef);
          const downloadsCount = downloadsSnapshot.size;

          let lastDownload = null;

          downloadsSnapshot.docs.forEach((doc) => {
            const data = doc.data();
            if (data.downloadedAt) {
              const dateObj =
                data.downloadedAt instanceof Timestamp
                  ? data.downloadedAt.toDate()
                  : new Date(data.downloadedAt);
              const dateKey = dateObj.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              });

              if (dailyStatsMap.has(dateKey)) {
                dailyStatsMap.get(dateKey).downloads += 1;
              }

              if (!lastDownload || dateObj > lastDownload.timestamp) {
                lastDownload = {
                  timestamp: data.downloadedAt,
                  userId: data.userId || doc.id,
                };
              }
            }
          });

          const likesRef = collection(db, "datasets", dataset.uuid, "likes");
          const likesSnapshot = await getDocs(likesRef);
          const likesCount = likesSnapshot.size;

          let lastLike = null;

          likesSnapshot.docs.forEach((doc) => {
            const data = doc.data();
            if (data.likedAt) {
              const dateObj =
                data.likedAt instanceof Timestamp
                  ? data.likedAt.toDate()
                  : new Date(data.likedAt);
              const dateKey = dateObj.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              });

              if (dailyStatsMap.has(dateKey)) {
                dailyStatsMap.get(dateKey).likes += 1;
              }

              if (!lastLike || dateObj > lastLike.timestamp) {
                lastLike = {
                  timestamp: data.likedAt,
                  userId: doc.id,
                };
              }
            }
          });

          const datasetScore =
            viewsCount * 0.5 + downloadsCount * 0.3 + likesCount * 0.2;

          return {
            datasetId: dataset.uuid,
            name: dataset.name || dataset.title || "Unnamed Dataset",
            views: viewsCount,
            downloads: downloadsCount,
            likes: likesCount,
            score: datasetScore,
            lastView,
            lastDownload,
            lastLike,
          };
        } catch (error) {
          console.warn(
            `Error fetching analytics for dataset ${dataset.uuid}:`,
            error
          );
          return {
            datasetId: dataset.uuid,
            name: dataset.name || "Unnamed",
            views: 0,
            downloads: 0,
            likes: 0,
            score: 0,
            lastView: null,
            lastDownload: null,
            lastLike: null,
          };
        }
      });

      const results = await Promise.all(allPromises);

      results.forEach((result) => {
        analyticsData.totalViews += result.views;
        analyticsData.totalDownloads += result.downloads;
        analyticsData.totalLikes += result.likes;

        analyticsData.viewsByDataset.push({
          datasetId: result.datasetId,
          name: result.name,
          views: result.views,
        });
        analyticsData.downloadsByDataset.push({
          datasetId: result.datasetId,
          name: result.name,
          downloads: result.downloads,
        });
        analyticsData.likesByDataset.push({
          datasetId: result.datasetId,
          name: result.name,
          likes: result.likes,
        });

        if (result.lastView) {
          analyticsData.recentActivity.push({
            type: "view",
            datasetId: result.datasetId,
            datasetName: result.name,
            timestamp: result.lastView.timestamp,
            userId: result.lastView.userId,
          });
        }
        if (result.lastDownload) {
          analyticsData.recentActivity.push({
            type: "download",
            datasetId: result.datasetId,
            datasetName: result.name,
            timestamp: result.lastDownload.timestamp,
            userId: result.lastDownload.userId,
          });
        }
        if (result.lastLike) {
          analyticsData.recentActivity.push({
            type: "like",
            datasetId: result.datasetId,
            datasetName: result.name,
            timestamp: result.lastLike.timestamp,
            userId: result.lastLike.userId,
          });
        }
      });

      // Sort by score for top performing datasets
      analyticsData.topPerformingDatasets = results
        .sort((a, b) => b.score - a.score)
        .slice(0, 5)
        .map((item) => ({
          name:
            item.name.length > 20
              ? item.name.substring(0, 17) + "..."
              : item.name,
          score: Math.round(item.score),
          views: item.views,
          downloads: item.downloads,
          likes: item.likes,
        }));

      analyticsData.uniqueViewers = analyticsData.uniqueViewers.size;

      analyticsData.viewsByDataset.sort((a, b) => b.views - a.views);
      analyticsData.downloadsByDataset.sort(
        (a, b) => b.downloads - a.downloads
      );
      analyticsData.likesByDataset.sort((a, b) => b.likes - a.likes);

      analyticsData.performanceOverTime = Array.from(dailyStatsMap.values());

      analyticsData.recentActivity
        .sort((a, b) => {
          const timeA =
            a.timestamp instanceof Timestamp
              ? a.timestamp.toMillis()
              : new Date(a.timestamp).getTime();
          const timeB =
            b.timestamp instanceof Timestamp
              ? b.timestamp.toMillis()
              : new Date(b.timestamp).getTime();
          return timeB - timeA;
        })
        .slice(0, 10)
        .forEach((activity) => {
          activity.timestamp = formatDate(activity.timestamp);
          activity.user = activity.userId
            ? activity.userId.slice(0, 8) + "..."
            : "Anonymous";
        });

      setAnalytics(analyticsData);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      setAnalytics({
        totalViews: 0,
        totalDownloads: 0,
        totalLikes: 0,
        uniqueViewers: 0,
        viewsByDataset: [],
        downloadsByDataset: [],
        likesByDataset: [],
        recentActivity: [],
        performanceOverTime: [],
        topPerformingDatasets: [],
      });
    }
  }, []);

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const datasetsQuery = query(
        collection(db, "datasets"),
        where("author", "==", id)
      );

      const datasetsSnapshot = await getDocs(datasetsQuery);

      const datasets = datasetsSnapshot.docs.map((doc) => ({
        id: doc.id,
        uuid: doc.id,
        ...doc.data(),
      }));

      setDataSets(datasets);
      await fetchUserAnalytics(datasets);
    } catch (error) {
      console.warn("Error fetching data:", error);
      setDataSets([]);
    } finally {
      setLoading(false);
    }
  }, [id, fetchUserAnalytics]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const refreshAnalytics = () => {
    fetchPosts();
  };

  // Prepare data for Top Datasets line chart
  const prepareTopDatasetsData = () => {
    return analytics.topPerformingDatasets.slice(0, 5).map((dataset) => ({
      name: dataset.name,
      views: dataset.views,
      downloads: dataset.downloads,
      likes: dataset.likes,
      total: dataset.views + dataset.downloads + dataset.likes,
    }));
  };

  // Prepare data for engagement comparison line chart
  const prepareEngagementComparisonData = () => {
    return [
      { type: "Views", value: analytics.totalViews, color: "#667eea" },
      { type: "Downloads", value: analytics.totalDownloads, color: "#10b981" },
      { type: "Likes", value: analytics.totalLikes, color: "#f59e0b" },
    ];
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip-kl5">
          <p className="tooltip-label-mn6">{label}</p>
          {payload.map((entry, index) => (
            <p
              key={index}
              className="tooltip-value-op7"
              style={{ color: entry.color }}
            >
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const getDatasetStats = (datasetId) => {
    return {
      views:
        analytics.viewsByDataset.find((v) => v.datasetId === datasetId)
          ?.views || 0,
      downloads:
        analytics.downloadsByDataset.find((d) => d.datasetId === datasetId)
          ?.downloads || 0,
      likes:
        analytics.likesByDataset.find((l) => l.datasetId === datasetId)
          ?.likes || 0,
    };
  };

  useEffect(() => {
    if (id !== auth.currentUser.uid) {
      navigate(`/`);
    }
  }, []);

  return (
    <div className="professional-dashboard-ajx">
      {/* Main Dashboard */}
      <div className="dashboard-main-op8">
        {/* Sidebar */}
        <aside className="dashboard-sidebar-qr9">
          <div className="sidebar-section-st0">
            <div className="user-card-wx2">
              <div className="user-avatar-container-yz3">
                <img
                  src={user?.photoURL}
                  alt="Profile"
                  className="user-avatar-ab4"
                />
              </div>
              <div className="user-info-ef6">
                <h3 className="user-fullname-gh7">{user?.displayName}</h3>
                <p className="user-role-ij8">Data Contributor</p>
              </div>
            </div>
          </div>

          <div className="sidebar-section-st0">
            <h4 className="section-label-uv1">Account Details</h4>
            <div className="detail-item-kl9">
              <span className="detail-label-mn0">
                Email
              </span>
              <span className="detail-value-qr2">{user?.email}</span>
            </div>
            <div className="detail-item-kl9">
              <span className="detail-label-mn0">
                 User ID
              </span>
              <div className="user-id-container-st3">
                <code className="user-id-value-uv4">
                  {auth.currentUser.uid}
                </code>
                <button
                  className="copy-btn-wx5"
                  onClick={copyUserId}
                  title="Copy User ID"
                >
                  {copied ? <FiCheck /> : <FiCopy />}
                </button>
              </div>
            </div>
          </div>

          <div className="sidebar-section-st0">
            <h4 className="section-label-uv1">
              Activity
            </h4>
            <div className="detail-item-kl9">
              <span className="detail-label-mn0">Joined</span>
              <span className="detail-value-qr2">
                {formatDate(metadata?.creationTime)}
              </span>
            </div>
            <div className="detail-item-kl9">
              <span className="detail-label-mn0">Last Active</span>
              <span className="detail-value-qr2">
                {formatDate(metadata?.lastSignInTime)}
              </span>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="dashboard-content-ef9">
          {loading ? (
            <div className="loading-state-gh0">
              <div className="loading-spinner-ij1"></div>
              <p>Loading analytics...</p>
            </div>
          ) : (
            <>
              {/* Summary Stats with Professional Color Indicators */}
              <div className="summary-stats-kl2">
                <div className="stat-card-mn3">
                  <div className="stat-content-st6">
                    <span
                      className="stat-number-profile-uv7"
                      style={{ color: "white" }}
                    >
                      {analytics.totalViews}
                    </span>
                    <span
                      className="stat-label-wx8"
                      style={{ color: "white" }}
                    >
                      Total Views
                      <div
                        style={{
                          marginTop: "8px",
                          width: "40px",
                          height: "3px",
                          background:
                            "linear-gradient(90deg, #667eea 0%, #8b5cf6 100%)",
                          borderRadius: "2px",
                        }}
                      ></div>
                    </span>
                  </div>
                </div>

                <div className="stat-card-mn3">
                  <div className="stat-content-st6">
                    <span
                      className="stat-number-profile-uv7"
                      style={{ color: "white" }}
                    >
                      {analytics.totalDownloads}
                    </span>
                    <span
                      className="stat-label-wx8"
                      style={{ color: "white" }}
                    >
                      Total Downloads
                      <div
                        style={{
                          marginTop: "8px",
                          width: "40px",
                          height: "3px",
                          background:
                            "linear-gradient(90deg, #10b981 0%, #0ea5e9 100%)",
                          borderRadius: "2px",
                        }}
                      ></div>
                    </span>
                  </div>
                </div>

                <div className="stat-card-mn3">
                  <div className="stat-content-st6">
                    <span
                      className="stat-number-profile-uv7"
                      style={{ color: "white" }}
                    >
                      {analytics.totalLikes}
                    </span>
                    <span
                      className="stat-label-wx8"
                      style={{ color: "white" }}
                    >
                      Total Likes
                      <div
                        style={{
                          marginTop: "8px",
                          width: "40px",
                          height: "3px",
                          background:
                            "linear-gradient(90deg, #f59e0b 0%, #f97316 100%)",
                          borderRadius: "2px",
                        }}
                      ></div>
                    </span>
                  </div>
                </div>
              </div>

              {/* Charts Section - All Line Charts */}
              <div className="charts-section-yz9">
                {/* Engagement Trend Over Time */}
                <div className="chart-card-ab0 full-width">
                  <div className="chart-header-cd1">
                    <h3 className="chart-title-ef2">
                      Daily Engagement Trend
                    </h3>
                    <div
                      style={{
                        display: "flex",
                        gap: "20px",
                        marginTop: "10px",
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            width: "12px",
                            height: "12px",
                            background: "#667eea",
                            borderRadius: "2px",
                          }}
                        ></div>
                        <span style={{ color: "#888", fontSize: "14px" }}>
                          Views
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            width: "12px",
                            height: "12px",
                            background: "#10b981",
                            borderRadius: "2px",
                          }}
                        ></div>
                        <span style={{ color: "#888", fontSize: "14px" }}>
                          Downloads
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            width: "12px",
                            height: "12px",
                            background: "#f59e0b",
                            borderRadius: "2px",
                          }}
                        ></div>
                        <span style={{ color: "#888", fontSize: "14px" }}>
                          Likes
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="chart-container-ij4">
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart responsive data={analytics.performanceOverTime}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                        <XAxis
                          dataKey="date"
                          stroke="#666"
                          tick={{ fill: "#888" }}
                        />
                        <YAxis
                          stroke="#666"
                          allowDecimals={false}
                          tick={{ fill: "#888" }}
                        />
                        <Tooltip
                          content={<CustomTooltip />}
                          contentStyle={{
                            background: "rgba(20, 20, 20, 0.95)",
                            border: "1px solid #333",
                            borderRadius: "8px",
                            backdropFilter: "blur(10px)",
                          }}
                        />
                       
                        <Line
                          type="monotone"
                          dataKey="views"
                          stroke="#667eea"
                          strokeWidth={3}
                          dot={{ r: 4, strokeWidth: 1, stroke: "#667eea" }}
                          activeDot={{
                            r: 6,
                            strokeWidth: 2,
                            stroke: "#667eea",
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="downloads"
                          stroke="#10b981"
                          strokeWidth={3}
                          strokeDasharray="5 5"
                          dot={{ r: 4, strokeWidth: 1, stroke: "#10b981" }}
                          activeDot={{
                            r: 6,
                            strokeWidth: 2,
                            stroke: "#10b981",
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="likes"
                          stroke="#f59e0b"
                          strokeWidth={3}
                          dot={{ r: 4, strokeWidth: 1, stroke: "#f59e0b" }}
                          activeDot={{
                            r: 6,
                            strokeWidth: 1,
                            stroke: "#f59e0b",
                          }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Two-column charts grid */}
                <div className="engagement-grid-nm0">
                  {/* Top Performing Datasets Line Chart */}
                  <div className="chart-card-ab0">
                    <div className="chart-header-cd1">
                      <h3 className="chart-title-ef2">
                        <FiStar className="chart-title-icon-gh3" />
                        Top Datasets Performance
                      </h3>
                    </div>
                    <div className="chart-container-ij4">
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={prepareTopDatasetsData()}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#2a2a2a"
                          />
                          <XAxis
                            dataKey="name"
                            stroke="#666"
                            tick={{ fill: "#888", fontSize: "12px" }}
                          />
                          <YAxis stroke="#666" tick={{ fill: "#888" }} />
                          <Tooltip
                            content={<CustomTooltip />}
                            contentStyle={{
                              background: "rgba(20, 20, 20, 0.95)",
                              border: "1px solid #333",
                              borderRadius: "8px",
                              backdropFilter: "blur(10px)",
                            }}
                          />
                          
                          <Line
                            type="monotone"
                            dataKey="views"
                            stroke="#667eea"
                            strokeWidth={2}
                            dot={{ r: 4, strokeWidth: 2, stroke: "#667eea" }}
                            activeDot={{
                              r: 6,
                              strokeWidth: 2,
                              stroke: "#ffffff",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="downloads"
                            stroke="#10b981"
                            strokeWidth={2}
                            dot={{ r: 4, strokeWidth: 2, stroke: "#10b981" }}
                            activeDot={{
                              r: 6,
                              strokeWidth: 2,
                              stroke: "#ffffff",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="likes"
                            stroke="#f59e0b"
                            strokeWidth={2}
                            dot={{ r: 4, strokeWidth: 2, stroke: "#f59e0b" }}
                            activeDot={{
                              r: 6,
                              strokeWidth: 2,
                              stroke: "#ffffff",
                            }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Engagement Comparison Line Chart */}
                  <div className="chart-card-ab0">
                    <div className="chart-header-cd1">
                      <h3 className="chart-title-ef2">
                        Engagement Comparison
                      </h3>
                    </div>
                    <div className="chart-container-ij4">
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={prepareEngagementComparisonData()}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#2a2a2a"
                          />
                          <XAxis
                            dataKey="type"
                            stroke="#666"
                            tick={{ fill: "#888" }}
                          />
                          <YAxis stroke="#666" tick={{ fill: "#888" }} />
                          <Tooltip
                            contentStyle={{
                              background: "rgba(20, 20, 20, 0.95)",
                              border: "1px solid #333",
                              borderRadius: "8px",
                              backdropFilter: "blur(10px)",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="value"
                            stroke="#8884d8"
                            strokeWidth={3}
                            dot={{
                              r: 6,
                              strokeWidth: 2,
                              stroke: "#8884d8",
                              fill: ({ color }) => color,
                            }}
                            activeDot={{
                              r: 8,
                              strokeWidth: 2,
                              stroke: "#ffffff",
                            }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="activity-section-qr8">
                <div className="section-header-st9">
                  <h3 className="section-title-uv0">
                    Recent Activity
                  </h3>
                </div>
                <div className="activity-list-yz2">
                  {analytics.recentActivity.length > 0 ? (
                    analytics.recentActivity.map((activity, index) => (
                      <div key={index} className="activity-item-ab3">
                        <div className="activity-content-ef5">
                          <div className="activity-text-gh6">
                            <span
                              className="activity-type-ij7"
                              style={{
                                color:
                                  activity.type === "view"
                                    ? "#667eea"
                                    : activity.type === "download"
                                    ? "#10b981"
                                    : "#f59e0b",
                                fontWeight: "600",
                              }}
                            >
                              {activity.type}
                            </span>{" "}
                            on
                            <span className="activity-dataset-kl8">
                              {" "}
                              {activity.datasetName}
                            </span>
                          </div>
                          <div className="activity-meta-mn9">
                            <span className="activity-user-op0">
                              User: {activity.user}
                            </span>
                            <span className="activity-time-qr1">
                              {activity.timestamp}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-activity-st2">
                      No recent activity to display
                    </div>
                  )}
                </div>
              </div>

              {/* Datasets Section */}
              <div className="datasets-section-uv3">
                <div className="section-header-wx4">
                  <h3 className="section-title-uv0">
                    My Datasets ({dataSets.length})
                  </h3>
                  
                </div>

                {dataSets.length > 0 ? (
                  <div className="datasets-grid-cd7">
                    {dataSets.map((dataset) => {
                      const stats = getDatasetStats(dataset.uuid);
                      return <Dataset datasetid={dataset.uuid} />;
                    })}
                  </div>
                ) : (
                  <div className="empty-state-mn2">
                    <div className="empty-state-content-op3">
                      <div className="empty-state-icon-container-qr4">
                      </div>
                      <h3>No datasets created yet</h3>
                      <p className="empty-state-description-uv6">
                        Start building your data portfolio by creating your
                        first dataset
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default Profile;
